import React, { useRef } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { MDXRenderer } from "gatsby-plugin-mdx"
import { motion } from "framer-motion"
import Underlining from "../styles/underlining"

import { useOnScreen } from "../hooks"
import Carousel from "react-material-ui-carousel"
import Icon from "./icons"
import Img from "gatsby-image"
import { lightTheme, darkTheme } from "../styles/theme"

const StyledProject = styled(motion.div)`
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
  align-items: center;
  margin-top: 0;
  flex-shrink: 0;
  margin-bottom: 6rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: space-between;
    flex-shrink: 1;
    margin-bottom: 10rem;
    /* Positioning of image and details should vary */
    flex-direction: row;
  }
  .details {
    width: 100%;
    max-width: 25rem;
    display: flex;
    flex-direction: column;
    margin-top: 3rem;
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      margin-top: 0;
    }
    .category {
      font-size: 0.875rem;
      line-height: 1rem;
      text-transform: uppercase;
      letter-spacing: +1px;
    }
    .title {
      margin-top: 0.625rem;
      margin-bottom: 0.625rem;
      font-size: 1.375rem;
      line-height: 1.625rem;
      font-weight: 700;
    }
    .tags {
      display: flex;
      flex-wrap: wrap;
      margin-top: 1.75rem;
      line-height: 1.2rem;
      span {
        margin-right: 1rem;
        margin-bottom: 1rem;
      }
    }
    .links {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      margin-top: 1rem;
      a {
        display: inline-block;
        margin-right: 2rem;
      }
      svg {
        width: 1.3rem;
        height: 1.3rem;
        transition: all 0.3s ease-out;
      }
      svg:hover {
        fill: ${({ theme }) => theme.colors.primary};
      }
    }
  }
  .screenshot {
    width: 100%;
    max-width: 25rem;
    margin-left: auto;
    margin-right: auto;
    height: 18.75rem;
    border-radius: ${({ theme }) => theme.borderRadius};
    box-shadow: 0 0 2.5rem rgba(0, 0, 0, 0.16);
    transition: all 0.3s ease-out;
    &:hover {
      transform: translate3d(0px, -0.125rem, 0px);
      box-shadow: 0 0 2.5rem rgba(0, 0, 0, 0.32);
    }
  }
  .text-content li {
    margin: 1rem 0 1rem -1.5rem;
    text-align: justify;
  }
`

const Project = ({ project, index, darkMode }) => {
  const positionIndex = index + 1
  const { body, frontmatter } = project.node
  const hasMoreThanOneScreenshot = frontmatter.screenshot.length > 1

  const tRef = useRef()
  const tOnScreen = useOnScreen(tRef)
  const tVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  }

  return (
    <motion.div
      ref={tRef}
      variants={tVariants}
      animate={tOnScreen ? "visible" : "hidden"}
    >
      <StyledProject key={index} position={positionIndex}>
        <div className="details">
          <div className="category">
            {frontmatter.emoji} {frontmatter.category}
          </div>
          <div className="title">{frontmatter.title}</div>
          <div className="text-content">
            <MDXRenderer>{body}</MDXRenderer>
          </div>
          <div className="tags">
            {frontmatter.tags.map(tag => (
              <Underlining key={tag} highlight>
                {tag}
              </Underlining>
            ))}
          </div>
          <div className="links">
            {frontmatter.github && (
              <a
                href={frontmatter.github}
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="External Link"
              >
                <Icon
                  name="github"
                  color={
                    darkMode
                      ? darkTheme.colors.subtext
                      : lightTheme.colors.subtext
                  }
                />
              </a>
            )}
            {frontmatter.external && (
              <a
                href={frontmatter.external}
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="External Link"
              >
                <Icon
                  name="external"
                  color={
                    darkMode
                      ? darkTheme.colors.subtext
                      : lightTheme.colors.subtext
                  }
                />
              </a>
            )}
          </div>
        </div>
        <div className="project-screenshots">
          <Carousel
            autoPlay={false}
            navButtonsAlwaysInvisible={!hasMoreThanOneScreenshot}
            indicators={hasMoreThanOneScreenshot}
          >
            {frontmatter.screenshot.map(image => (
              <div key={image} style={{ padding: "2.5rem" }}>
                <Img
                  className="screenshot"
                  fluid={image.childImageSharp.fluid}
                  imgStyle={{
                    objectFit: "contain",
                  }}
                />
              </div>
            ))}
          </Carousel>
        </div>
      </StyledProject>
    </motion.div>
  )
}

Project.propTypes = {
  darkMode: PropTypes.bool,
  project: PropTypes.object,
  index: PropTypes.number,
}

export default Project
